import {
  VehicleState,
  UpdateVehicle,
  CreateVehicle,
} from "./../../types/vehicles";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { suggestionsAPI, apiResource } from "../../services/api";

const vehiclesApi = apiResource("vehicles");

const initialState: VehicleState = {
  vehicles: [],
  currentVehicle: null,
  suggestions: [],
  loading: false,
  error: null,
  totalFound: 0,
  searchQuery: "",
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
};

// Get Vehicle Suggestions
export const getVehiclesSuggestions = createAsyncThunk(
  "vehicle/getVehiclesSuggestions",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await suggestionsAPI.getVehicles(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch vehicle suggestions"
      );
    }
  }
);

export const getVehicles = createAsyncThunk(
  "vehicle/getVehicles",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await vehiclesApi.getAll(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch vehicle"
      );
    }
  }
);

export const getVehicle = createAsyncThunk(
  "vehicle/getVehicle",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await vehiclesApi.getOne(id);
      return response.data.data.vehicle;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch vehicle"
      );
    }
  }
);

export const createVehicle = createAsyncThunk(
  "vehicle/createVehicle",
  async (vehicleData: CreateVehicle, { rejectWithValue }) => {
    try {
      const response = await vehiclesApi.create(vehicleData);
      return response.data.data.vehicle;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create vehicle"
      );
    }
  }
);

export const updateVehicle = createAsyncThunk(
  "vehicle/updateVehicle",
  async (
    { id, vehicleData }: { id: string; vehicleData: UpdateVehicle },
    { rejectWithValue }
  ) => {
    try {
      const response = await vehiclesApi.update(id, vehicleData);
      return response.data.data.vehicle;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update vehicle"
      );
    }
  }
);

export const deleteVehicle = createAsyncThunk(
  "vehicle/deleteVehicle",
  async (id: string, { rejectWithValue }) => {
    try {
      await vehiclesApi.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete vehicle"
      );
    }
  }
);

const vehicleSlice = createSlice({
  name: "vehicles",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
      state.totalFound = 0;
      state.searchQuery = "";
    },
    clearCurrentVehicle: (state) => {
      state.currentVehicle = null;
    },
    resetVehicleState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Vehicle Suggestions
      .addCase(getVehiclesSuggestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVehiclesSuggestions.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestions = action.payload.suggestions;
        state.totalFound = action.payload.totalFound;
        state.searchQuery = action.payload.searchQuery;
        state.error = null;
      })
      .addCase(getVehiclesSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.suggestions = [];
        state.totalFound = 0;
      })
      // Get vehicles
      .addCase(getVehicles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVehicles.fulfilled, (state, action) => {
        state.loading = false;
        state.vehicles = action.payload.vehicles;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getVehicles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Vehicle
      .addCase(getVehicle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVehicle.fulfilled, (state, action) => {
        state.loading = false;
        state.currentVehicle = action.payload;
        state.error = null;
      })
      .addCase(getVehicle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create Vehicle
      .addCase(createVehicle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createVehicle.fulfilled, (state, action) => {
        state.loading = false;
        state.vehicles.unshift(action.payload);
        state.error = null;
      })
      .addCase(createVehicle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Vehicle
      .addCase(updateVehicle.fulfilled, (state, action) => {
        const index = state.vehicles.findIndex(
          (vehicle) => vehicle._id === action.payload._id
        );
        if (index !== -1) {
          state.vehicles[index] = action.payload;
        }
        if (state.currentVehicle?._id === action.payload._id) {
          state.currentVehicle = action.payload;
        }
      })
      // Delete Vehicle
      .addCase(deleteVehicle.fulfilled, (state, action) => {
        state.vehicles = state.vehicles.filter(
          (vehicle) => vehicle._id !== action.payload
        );
        state.pagination.total -= 1;
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteVehicle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteVehicle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSuggestions, resetVehicleState } =
  vehicleSlice.actions;
export default vehicleSlice.reducer;
